<template lang="pug">
  management-page.scrollable(title="Summary"
                :creatable="false"
                :searchable="false"
                :showHeader="false")
    template(v-slot:body)
      aiq-row(:gutter="16" justify="end")
          .time-range-filter
            aiq-date-picker(v-model="dateRange"
                          @change="handleDatePickerChange($event)"
                          type="daterange"
                          placeholder="Select date range",
                          popper-class="metrics-time-range__date-picker-popup"
                          :shortcuts="datePickerOptions.shortcuts"
                          :disabled-date="datePickerOptions.disabledDate"
                          :teleported="false"
                          format="YYYY/MM/DD"
                          :clearable="false")
      .chart-grid
          aiq-row(:gutter="16")
              aiq-col(:span="24")
                  overview-chart(v-if="configs.active_customers"
                          :dateRange="dateRangeUtc"
                          :getTitleFromResponse="getTitleFromResponse"
                          :combineResponseConfigTitles="combineResponseConfigTitles"
                          :breakdown="selectedBreakdownOption.value"
                          :filters="selectedFilters"
                          :chartConfig="activePlatformCustomersChartConfig"
                          :formatXTick="xLabelsActiveCustomerFormat"
                          :shouldHandlePartialDates="true"
                          @onXLabelsChange="xLabelsActiveCustomer=$event"
                          @onChartDataChange="onChartDataChange($event,'activeCustomersChartData')"
                          yLabel="Count"
                          grouped=false
                          visualization='aiq-bar-chart'
                          :reportFileName="returnReportFileName($t(`metrics_tab.plaform_tab.summary_tab.active_customers.title`))"
                          :filteredExportColumns="['daily']"
                          :relabeledExportHeaders="activePlatformCustomersChartConfig.relabeledExportHeaders"
                          :useDebounceForLoadMetrics="true"
                          )
      .cal-box(v-if="configs.daily_conversations")
        .cal-toolbar
          .time-filter-box-container
            confirmable-time-picker.el-input__inner(
                    is-range
                    :model-value="timeRangeFilter"
                    ref="filter_start_time"
                    name="filter_start_time"
                    @change="onTimeFilterChange")
          .title
            h4
             | Daily Conversation Count ({{timezone}})
             aiq-tooltip.help-icon(placement="left")
               template(v-slot:content)
                 div(v-html="convoCountHelpText")
               i.iq-ico-explanation
          .download-button
            aiq-button.export(@click="exportTable")
              i.iq-ico-export
        .cal-grid
          .cal-counts(v-for=("convoCountChunk in convoCountsChunks"))
            aiq-row(:gutter="20" type="flex")
              .cal-counts(v-for=("monthData in convoCountChunk"))
                  aiq-col(:lg="30" :span="24")
                    overview-chart(
                      :filters="selectedFilters"
                      :chartConfig="calendarChartConfig"
                      :preLoadedchartData="monthData.data_points"
                      :keepChartOnly="true"
                      :breakdown="selectedBreakdownOption.value"
                      visualization="aiq-cal-heatmap")
      include ../selectableFilters/SelectableFilters.pug
      .chart-grid
        aiq-row(:gutter="16")
          aiq-col(:span="24")
            overview-chart(v-if="configs.conversations_by_interaction"
                          :dateRange="dateRangeUtc"
                          :filters="selectedFilters"
                          :chartConfig="conversationbyInteractionChartConfig"
                          :formatXTick="xAxisFormatForInteraction"
                          yLabel="Percent"
                          :yScaleMin=0
                          :yScaleMax=100
                          :formatYTick="percentFormatter"
                          grouped=true
                          shouldShowTotalOnTooltip=true
                          visualization='aiq-bar-chart'
                          :reportFileName="returnReportFileName($t(`metrics_tab.plaform_tab.summary_tab.conversation_interaction.title`))"
                          @onChartDataChange="onChartDataChange($event, 'interaction')"
                          @onChartBreakdownChange="chosenInteractionBucketOption=$event"
                          :useDebounceForLoadMetrics="true"
                          :shouldHandlePartialDates="true"
                          @onXLabelsChange="xLabelsInteraction=$event"
                          :getOriginalData="originalInteractionChartData"
                          )
          aiq-col(:span="12")
            overview-chart(v-if="configs.conversations"
                          :dateRange="dateRangeUtc"
                          :filters="selectedFilters"
                          :chartConfig="conversationActivityChartConfig"
                          yLabel="Count"
                          grouped
                          :reportFileName="returnReportFileName('Conversation Activity')"
                          @onChartDataChange="onChartDataChange($event, 'activity')"
                          :formatXTick="xAxisFormatForActivity"
                          @onChartBreakdownChange="chosenActivityBucketOption=$event"
                          :useDebounceForLoadMetrics="true"
                          :shouldHandlePartialDates="true"
                          @onXLabelsChange="xLabelsActivity=$event"
                          )

          aiq-col(:span="12")
            overview-chart(v-if="configs.hourly_new_conversations"
                          :dateRange="dateRangeUtc"
                          :breakdown="selectedBreakdownOption.value"
                          :filters="selectedFilters"
                          :chartConfig="hourlyConversationsChartConfig"
                          visualization="aiq-heat-map"
                          :downloadWithTimeTicks="false"
                          :reportFileName="returnReportFileName('Hourly Conversation Volume')"
                          :style="{ height: '100%' }"
                          :useDebounceForLoadMetrics="true"
                          )
      .chart-grid
        aiq-row(:gutter="16")
          aiq-col(:span="24")
            summary-metrics-summary-table(:dateRange="dateRangeUtc"
                                          :breakdown="selectedBreakdownOption.value"
                                          :selectedFilters="selectedFilters")

</template>

<script>
import
{ mapGetters } from 'vuex';
import dayjs from '@/localPlugins/dayjs';
import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';
import isUndefined from 'lodash/isUndefined';
import chunk from 'lodash/chunk';
import cloneDeep from 'lodash/cloneDeep';
import ManagementPage from '@/components/ManagementPage/ManagementPage.vue';
import OverviewChart from '@/components/Chart.vue';
import CollapsibleSelect from '@/components/CollapsibleSelect/CollapsibleSelect.vue';
import SummaryMetricsSummaryTable from './SummaryMetricsSummaryTable.vue';
import activePlatformCustomersChartConfig from '../../../configs/activePlatformCustomersChartConfig';
import COLORS from '../../../configs/colors';
import ConfirmableTimePicker from '@/components/ConfirmableTimePicker.vue';

import conversationActivityChartConfig from '../../../configs/conversationActivityMixin';
import PlatformFiltersMixin from '../selectableFilters/SelectableFilters';
import ChartsConfigurationsMixin from '../../../configs/chartsConfigurationsMixin';
import debounce from 'lodash/debounce';
import { createPickerDateRangeOption, datePickerDefaultOptions, lastAvailableMonthAndQuarterRange, dstDateRangeUtc } from '@/pages/Metrics/configs/dateRange';
import { downloadAsFile, getHoursMinutesString, useTimeZone, unixStampToTzAttachedMoment } from '@/libs';
import { getHoursAndMinutes, MAX_HOURS_FOR_DATE, MAX_MINUTES_FOR_DATE, setHoursAndMinutes } from '@/libs/time';

const calendarChartConfig = {
  name: 'daily_conversations',
  title: 'Daily Conversation Count',
  metric: 'daily_conversations',
  graphParams: {
    x: 'time_epoch',
    y: 'num_conversations',
    verticalOrientation: false,
    range: 1,
    color: {
      type: 'linear',
      scheme: 'Oranges',
    },
    domain: {
      type: 'month',
      // eslint-disable-next-line no-magic-numbers
      padding: [10, 10, 0, 0],
      label: {
        position: 'top',
        fontSize: 16,
        text: 'MMM-YYYY',
      },
    },
    subdomain: {
      type: 'xDay',
      radius: 2,
      width: 30,
      height: 30,
      label: (t, v) => v,
    },
    LegendConfig:
      // eslint-disable-next-line no-magic-numbers
      { width: 500 },
    TooltipConfig: {
      text: function(date, value, dayjsDate) {
        return (
          `${value ? value : 'No data' } on ${ dayjsDate.format('L')}`
        );
      },
    },
    CalendarLabelConfig: {
      width: 30,
      textAlign: 'middle',
      text: () => dayjs.weekdaysShort(),
      position: 'top',
      key: 'weekdays',
    },
  },
};
const unresolvedChartConfig = {
  name: 'unresolved_conversations',
  title: 'Unresolved Type',
  metric: 'unresolved_conversations',
  lines: {
    unresolved_in_queue: {
      label: 'Queue Disconnected',
      position: 1,
      color: COLORS.red,
    },
    unresolved_by_agent: {
      label: 'Timeout with Agent',
      position: 2,
      color: COLORS.orange,
    },
    unresolved_by_ai: {
      label: 'Timeout with AI',
      position: 3,
      color: COLORS.yellow,
    },
    unresolved_email_shown: {
      label: 'Email Discard',
      position: 4,
      color: COLORS.grey,
    },
    unresolved_email_submit: {
      label: 'Email Sent',
      position: 5,
      color: COLORS.blue,
    },
  },
  helpText: `<div>
      Breakdown of unresolved conversations.<br><br>
      <b>Queue Disconnected</b>: Customer disconnects while waiting in queue<br><br>
      <b>Agent Disconnnected</b>: Customer disconnects while talking with human agent<br><br>
      <b>AI Disconnected</b>: Customer disconnects while talking with AI<br><br>
      <b>Email Discard</b>: Customer is shown an email form but doesn’t submit<br><br>
      <b>Email Sent</b>: Customer is shown an email form and submits</div>`,
};

const responseTimeChartConfig = {
  name: 'first_response_times',
  title: 'First Response Time',
  metric: 'conversations_times',
  lines: {
    max_first_agent_reply_time: {
      label: 'Max',
      position: 1,
      type: 'dashed',
      color: COLORS.red,
    },
    median_first_agent_reply_time: {
      label: 'Median',
      position: 2,
      type: 'line',
      color: COLORS.orange,
    },
    min_first_agent_reply_time: {
      label: 'Min',
      position: 3,
      type: 'dashed',
      color: COLORS.blue,
    },
  },
  helpText:
    'The median of a team’s/agent’s first response time from the first inbound message to the first reply. Displays the median first chat response time started as a function of time.',
};

const hourlyConversationsChartConfig = {
  name: 'hourly_new_conversations',
  title: 'Hourly Conversation Volume',
  metric: 'metrics_by_hour',
  lines: {
    metrics_by_hour: {
      valueKey: 'median_conversation_volume',
      xKey: 'hour',
      yKey: 'day',
    },
  },
  helpText: 'Displays the weekly volume maximum. Colors show conversation volume.<br>Hover to see the weekly conversation average, by hour.',
  exportType: 'tabular_chart_data',
};

const queueTimeChartConfig = {
  name: 'queue',
  title: 'Queue',
  metric: 'queue',
  lines: {
    max_queue_time: {
      label: 'Max Queue Time',
      position: 1,
      type: 'dashed',
      color: COLORS.red,
    },
    median_queue_time: {
      label: 'Median Queue Time',
      position: 2,
      type: 'line',
      color: COLORS.orange,
    },
    queue_size: {
      label: 'Median Queue Size',
      position: 3,
      axis: 'y2',
      type: 'bar',
      color: COLORS.blue,
    },
  },
  helpText: `<div>
      Displays the median queue size with the median and max queue times for all queued conversations.<br><br>
      <b>Queue</b>: When a customer is waiting to be transferred from the Virtual Assistant to an agent.<br><br>
      <b>Queue Size</b>: How many customers are waiting in the queue.<br><br>
      <b>Queue Time</b>: Time customers wait in the queue.
      </div>`,
};

const resolutionTimeChartConfig = {
  name: 'conversation_resolution_times',
  title: 'Median Resolution Time',
  metric: 'conversations_times',
  lines: {
    median_ai_handle_time: {
      label: 'AI Handle Time',
      position: 1,
      type: 'area-spline',
      color: COLORS.orange,
    },
    median_agent_handle_time: {
      label: 'Agent Handle Time',
      position: 2,
      type: 'area-spline',
      color: COLORS.red,
    },
    median_resolution_time: {
      label: 'Resolution Time',
      position: 3,
      type: 'area-spline',
      color: COLORS.blue,
    },
  },
  helpText: `<div>
    <b>Agent Handle Time</b>: Median time between agent taking on a conversation until that conversation is completed.<br><br>
    <b>AI Handle Time</b>: Median time of AI handling the conversation.<br><br>
    <b>Resolution Time</b>: Total time from start of a conversation until it is completed.
    </div>`,
};

const conversationRatingsChartConfig = {
  name: 'survey_resolution',
  title: 'Overall Conversation Resolutions',
  metric: 'survey_resolution',
  lines: {
    no: {
      label: 'No',
      position: 1,
      color: COLORS.red,
    },
    no_reply: {
      label: 'No Reply',
      position: 2,
      color: COLORS.orange,
    },
    yes: {
      label: 'Yes',
      position: 3,
      color: COLORS.yellow,
    },
  },
  helpText: 'Displays the extent to which customer believes inquiry was solved.',
};


export default {
  pageResource: '/metrics/platform',
  components: {
    SummaryMetricsSummaryTable,
    ManagementPage,
    OverviewChart,
    CollapsibleSelect,
    ConfirmableTimePicker,
  },
  async mounted() {
    try {
      await this.loadCalendarConvoCounts();
      await this.loadAllFilterOptions();
      this.timezone = await useTimeZone(
        Intl.DateTimeFormat().resolvedOptions().timeZone,
        this.$store.dispatch,
        this.$store._state.data.configs,
      );
    } catch (err) {
      this.$aiq.notify.error(`Failed: ${err.message}`);
    }
  },
  watch: {
    dateRange: debounce(async function onDateChange(newValue, oldValue = []) {
      const [start, end] = oldValue;
      if (!(start && end)) {
        return;
      }
      await this.loadAllFilterOptions();
      await this.loadCalendarConvoCounts();
    }, 1),
  },
  mixins: [PlatformFiltersMixin, ChartsConfigurationsMixin, conversationActivityChartConfig],
  data() {
    const chartConfigs = [
      activePlatformCustomersChartConfig,
    ];

    for (const config of chartConfigs) {
      config.title = this.$t(`metrics_tab.plaform_tab.summary_tab.${config.name}.title`, config.title);
    }
    const defaultStartTimeDate = setHoursAndMinutes(new Date(), 0, 0);
    const defaultEndTimeDate = setHoursAndMinutes(new Date(), MAX_HOURS_FOR_DATE, MAX_MINUTES_FOR_DATE);
    const timeRangeFilter = [defaultStartTimeDate, defaultEndTimeDate];
    const [defaultStartTime, defaultEndTime, defaultStartTimeLabel, defaultEndTimeLabel] = this.getStartEndTimes(timeRangeFilter);
    return {
      timezone: null,
      timeRangeFilter,
      convoCountsChunkSize: 3,
      convoCountsChunks: [],
      activePlatformCustomersChartConfig,
      unresolvedChartConfig,
      responseTimeChartConfig,
      resolutionTimeChartConfig,
      calendarChartConfig,
      hourlyConversationsChartConfig,
      queueTimeChartConfig,
      conversationRatingsChartConfig,
      convoCountHelpText: `The Daily Conversation Count graph displays the number of conversations for each day within
   the selected date range. If a specific time range is selected, the counts reflect that time period for each day such
   that the last minute is included fully.`,
      conversationbyAgentsResponseFields: [
        'multiple_agents',
        'single_agents',
        'zero_agents',
        'single_bots',
        'zero_bots',
        'total_conversations',
      ],
      conversationbyBotsResponseFields: [
        'single_bots',
        'zero_bots',
        'total_conversations',
      ],
      conversationbyCustomersResponseFields: ['single_customers', 'zero_customers', 'total_conversations'],
      percentFormatter: v => `${v}%`,
      durationFormatter: this.$aiq.formatters.timeDelta,
      activityChartData: [],
      interactionChartData: [],
      xLabelsActiveCustomer: {},
      activeCustomersChartData: [],
      convoCountsData: [],
      xLabelsInteraction: {},
      xLabelsActivity: {},
      originalInteractionChartData: {},
      originalActivityChartData: {},
      start_time: defaultStartTime,
      end_time: defaultEndTime,
      start_time_label: defaultStartTimeLabel,
      end_time_label: defaultEndTimeLabel,
    };
  },
  computed: {
    ...mapGetters({
      configs: 'configs/graphsConfigs',
    }),
    datePickerOptions() {
      return {
        ...datePickerDefaultOptions(),
        shortcuts: lastAvailableMonthAndQuarterRange.map(createPickerDateRangeOption),
      };
    },
    dateRangeUtc() {
      return dstDateRangeUtc(this.dateRange);
    },
  },
  methods: {
    makeDateTimeLabel() {
      const [period_start, period_end] = this.dateRangeUtc;
      const start_date = unixStampToTzAttachedMoment(period_start, this.timezone).format('YYYY-MM-DD');
      const end_date = unixStampToTzAttachedMoment(period_end, this.timezone).format('YYYY-MM-DD');
      return `${start_date}_to_${end_date}_between_${this.start_time_label}_and_${this.end_time_label}`;
    },
    exportTable() {
      downloadAsFile({
        dataType: 'data_with_all_datapoints',
        dataSource: 'metrics',
        filename: `daily_conversation_counts_${this.makeDateTimeLabel()}`,
        data: {dataset: this.prepareCalData(this.convoCountsData)},
      });
    },
    getStartEndTimes(timeRange) {
      let start_time = '';
      let end_time = '';
      let start_time_label = '';
      let end_time_label = '';
      if (!isEmpty(timeRange) && !isUndefined(timeRange[0]) && !isUndefined(timeRange[1])) {
        // timeRange here is an array of Date objects in browser time, which is expected
        const startHoursMinutes = getHoursAndMinutes(timeRange[0]);
        const endHoursMinutes = getHoursAndMinutes(timeRange[1]);
        start_time_label = getHoursMinutesString(startHoursMinutes.hours, startHoursMinutes.minutes);
        end_time_label = getHoursMinutesString(endHoursMinutes.hours, endHoursMinutes.minutes);
        if (endHoursMinutes.minutes === MAX_MINUTES_FOR_DATE) {
          endHoursMinutes.hours = endHoursMinutes.hours + 1;
          endHoursMinutes.minutes = 0;
        }
        else {
          endHoursMinutes.minutes = endHoursMinutes.minutes + 1;
        }
        // We add one minute to make sure that backend includes the full minute
        // backend supports 24:00 to capture data
        // between 23:59 and 00:00 of the next day
        start_time = getHoursMinutesString(startHoursMinutes.hours, startHoursMinutes.minutes);
        end_time = getHoursMinutesString(endHoursMinutes.hours, endHoursMinutes.minutes);
      }
      return [start_time, end_time, start_time_label, end_time_label];
    },
    async onTimeFilterChange(timeRange) {
      const [start_time, end_time, start_time_label, end_time_label] = this.getStartEndTimes(timeRange);
      this.start_time = start_time;
      this.end_time = end_time;
      this.start_time_label = start_time_label;
      this.end_time_label = end_time_label;
      await this.loadCalendarConvoCounts();
    },
    async loadCalendarConvoCounts() {
      const [period_start, period_end] = this.dateRangeUtc;
      if (!(period_start && period_end && this.selectedBreakdownOption)) {
        return;
      }
      const params = {
        period_start: period_start,
        period_end: period_end,
        filters: this.selectedFilters,
        bucket: this.selectedBreakdownOption.value,
      };
      if (this.start_time && this.end_time) {
        params.start_time = this.start_time;
        params.end_time = this.end_time;
      }
      return this.$store
        .dispatch('metrics/getMetric', ['daily_conversations', params])
        .then(data => {
          this.convoCountsData = get(data, 'dataset', []);
          this.convoCountsChunks = chunk(this.convoCountsData, this.convoCountsChunkSize);
        });
    },
    xLabelsActiveCustomerFormat(date) {
      return this.xAxisFormatter(date, this.xLabelsActiveCustomer, 'activeCustomersChartData');
    },
    getTitleFromResponse(response) {
      const obj = get(get(response, 'dataset', []).filter(dp => dp.metric === 'total_active'),
        '0.data_points.0', {});
      return get(obj, 'ys.0.value', '');
    },
    combineResponseConfigTitles(configTitle, responseTitle) {
      if (isEmpty(configTitle)) {
        return '';
      }
      if (isUndefined(responseTitle)) {
        return configTitle;
      }
      return `${responseTitle} ${configTitle}`;
    },
    returnReportFileName(chartName = 'data') {
      const [startTimestamp, endTimestamp] = this.dateRange;
      const startDate = dayjs.isDayjs(startTimestamp)
        ? startTimestamp.format('YYYY-MM-DD')
        : dayjs(startTimestamp).format('YYYY-MM-DD');
      const endDate = dayjs.isDayjs(endTimestamp)
        ? endTimestamp.format('YYYY-MM-DD')
        : dayjs(endTimestamp).format('YYYY-MM-DD');
      return `${chartName.replaceAll(' ', '_')}_${startDate}_${endDate}`;
    },
    onChartDataChange(data, type) {
      const dataPoints = data?.dataset?.[0]?.data_points || [];
      if (type === 'interaction') {
        this.interactionChartData = dataPoints;
        this.originalInteractionChartData = cloneDeep(data);
      }
      else {
        this.activityChartData = dataPoints;
        this.originalActivityChartData = cloneDeep(data);
      }
    },
    isDataPartial(date, type) {
      const timeStamp = new Date(date).getTime();
      const chartData = type === 'interaction' ? this.interactionChartData : this.activityChartData;
      const isPartial = chartData.find(item => item.x === timeStamp)?.isPartial;
      return isPartial;
    },
    xAxisFormatForInteraction(date) {
      return this.xAxisFormatter(date, this.xLabelsInteraction, 'interaction');
    },
    xAxisFormatForActivity(date) {
      return this.xAxisFormatter(date, this.xLabelsActivity, 'activity');
    },
    xAxisFormatter(date, labelMap, dataType) {
      const timestamp = dayjs(date).valueOf();
      const label = labelMap[timestamp];
      const partialIndicator = this.isDataPartial(date, dataType) ? ' *' : '';
      return `${label || dayjs(date).format('MMM DD')}${partialIndicator}`;
    },
    formatCalDataPoints(dataPoints) {
      const data = [];
      dataPoints.forEach((item) => {
        const { time_epoch, num_conversations} = item;
        data.push({
          date: dayjs(time_epoch).format('MMM DD, YYYY'),
          conversations: num_conversations,
        });
      });
      return data;
    },
    prepareCalData(dataSet) {
      return dataSet.map(item => ({data_points: this.formatCalDataPoints(item.data_points)}));
    },
  },
};
</script>

<style lang="scss" scoped>
@import "../../../../../styles/aiq-variables.scss";
@import "../selectableFilters/SelectableFilters.scss";

.scrollable {
  overflow-y: auto;
}
:deep(.ch-domain-bg) {
  background-color: var(--aiq-color-white);
}
.el-card:not(:first-of-type) {
  margin-top: 24px;
}

.time-range-filter {
  position: relative;
  margin-bottom: 24px;
}
:deep(div.time-filter-box-container) {
    width: 250px;
}
:deep(.c3-circles-total_convos) {
opacity: 0 !important;
}

.chart-configuration {
  display: flex;
  margin-bottom: 16px;
  justify-content: space-between;

  .breakdown {
    display: flex;
    align-items: center;
    flex-grow: 1;
  }

  .members-filter {
    display: flex;
    align-items: center;
  }

  .export-command {
    display: flex;
    height: 30px;
    width: 40px;
    justify-content: center;
    align-items: center;
    margin-left: 16px;
    padding: 0;
  }
}
.cal-box {
  border: 1px solid #d1dbe5;
  border-radius: 3px;
  display: flex;
  padding: 20px;
  justify-content: center;
  flex-direction: column;
  .cal-grid {
    display: grid;
    place-items: center;
    .cal-counts {
      margin-bottom: 20px;
      display: block;
    }
  }

  .cal-toolbar {
    display: flex;
    margin-bottom: 54px;
    justify-content: space-between;
    position: relative;
    .title{
      position: absolute;
      left: 50%;
      transform: translateX(-50%);
      margin-left: 0;
      font-weight: bold;
    }
    .download-button {
      margin-left: auto;
      height: 4px;
    }
    .time-filter-box-container {
      margin-right: 100px;
      height: 4px;
    }
    .iq-ico-explanation {
      margin-left: 8px;
    }
  }
}
</style>
